import { readAuthSession } from "@tensilelabs/auth-client";
import { apiBaseUrl, authToken } from "../api";
import type {
  ArtifactRecord,
  DashboardBindings,
  DashboardPage,
  PostRunProposal,
  RunEventRecord,
  RunIntent,
  RunLogEntry,
  RunStepRecord,
  Task,
  TaskRun,
} from "./runTypes";

/** The authenticated tenant; the demo UUID is only a safety net for direct component tests. */
export function currentAccountId(): string {
  return readAuthSession()?.accountId ?? "00000000-0000-0000-0000-000000000001";
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl()}${path}`, {
    ...init,
    headers: {
      authorization: `Bearer ${authToken()}`,
      ...(init?.body ? { "content-type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) {
    const message = await response.text().catch(() => "");
    throw new Error(message || `${response.status} ${response.statusText}`);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const runApi = {
  getNotificationEmail(accountId: string): Promise<{ email?: string | null }> {
    return request(`/api/v1/account/notification-email?account_id=${accountId}`);
  },

  setNotificationEmail(accountId: string, email: string): Promise<{ email: string }> {
    return request(`/api/v1/account/notification-email`, {
      method: "PUT",
      body: JSON.stringify({ account_id: accountId, email }),
    });
  },

  dashboard(accountId: string, status?: string, cursor?: string): Promise<DashboardPage> {
    const params = new URLSearchParams({ account_id: accountId });
    if (status) params.set("status", status);
    if (cursor) params.set("cursor", cursor);
    return request(`/api/v1/dashboard?${params.toString()}`);
  },

  listTasks(accountId: string): Promise<Task[]> {
    return request(`/api/v1/tasks?account_id=${accountId}`);
  },

  getTask(accountId: string, taskId: string): Promise<Task> {
    return request(`/api/v1/tasks/${taskId}?account_id=${accountId}`);
  },

  setTaskEnabled(accountId: string, taskId: string, enabled: boolean): Promise<Task> {
    return request(`/api/v1/tasks/${taskId}/enabled`, {
      method: "POST",
      body: JSON.stringify({ account_id: accountId, enabled }),
    });
  },

  listRuns(accountId: string, taskId?: string): Promise<TaskRun[]> {
    const params = new URLSearchParams({ account_id: accountId, limit: "20" });
    if (taskId) params.set("task_id", taskId);
    return request(`/api/v1/runs?${params.toString()}`);
  },

  getRun(accountId: string, runId: string): Promise<TaskRun> {
    return request(`/api/v1/runs/${runId}?account_id=${accountId}`);
  },

  launch(
    accountId: string,
    taskId: string,
    args: { run_intent?: RunIntent; inputs?: Record<string, unknown>; preflight_id?: string },
  ): Promise<TaskRun> {
    return request(`/api/v1/tasks/${taskId}/runs`, {
      method: "POST",
      body: JSON.stringify({
        account_id: accountId,
        inputs: args.inputs ?? {},
        run_intent: args.run_intent,
        preflight_id: args.preflight_id,
      }),
    });
  },

  cancel(accountId: string, runId: string): Promise<TaskRun> {
    return request(`/api/v1/runs/${runId}/cancel`, {
      method: "POST",
      body: JSON.stringify({ account_id: accountId }),
    });
  },

  requestReview(accountId: string, runId: string): Promise<{ queued: boolean }> {
    return request(`/api/v1/runs/${runId}/review`, {
      method: "POST",
      body: JSON.stringify({ account_id: accountId }),
    });
  },

  rerun(accountId: string, runId: string): Promise<TaskRun> {
    return request(`/api/v1/runs/${runId}/rerun`, {
      method: "POST",
      body: JSON.stringify({ account_id: accountId }),
    });
  },

  steps(accountId: string, runId: string, after = 0): Promise<RunStepRecord[]> {
    return request(`/api/v1/runs/${runId}/steps?account_id=${accountId}&after=${after}`);
  },

  logs(accountId: string, runId: string, after = 0): Promise<RunLogEntry[]> {
    return request(`/api/v1/runs/${runId}/logs?account_id=${accountId}&after=${after}`);
  },

  bindings(accountId: string, runId: string): Promise<DashboardBindings> {
    return request(`/api/v1/runs/${runId}/dashboard?account_id=${accountId}`);
  },

  getTemplateDetail(accountId: string, templateId: string): Promise<import("./runTypes").TemplateDetail> {
    return request(`/api/v1/templates/${templateId}?account_id=${accountId}`);
  },

  workerSession(accountId: string, taskId: string): Promise<import("./runTypes").WorkerSession> {
    return request(`/api/v1/tasks/${taskId}/session?account_id=${accountId}`);
  },

  takeover(
    accountId: string,
    taskId: string,
    sessionToken: string,
    reason: string,
  ): Promise<import("./runTypes").WorkerSession> {
    return request(`/api/v1/tasks/${taskId}/takeover`, {
      method: "POST",
      body: JSON.stringify({
        account_id: accountId,
        session_token: sessionToken,
        user_id: null,
        reason,
      }),
    });
  },

  handback(accountId: string, taskId: string, sessionToken: string) {
    return request(`/api/v1/tasks/${taskId}/handback`, {
      method: "POST",
      body: JSON.stringify({ account_id: accountId, session_token: sessionToken }),
    });
  },

  preflight(args: {
    accountId: string;
    templateId: string;
    versionId: string;
    intent: RunIntent;
    inputs: Record<string, unknown>;
  }): Promise<import("./runTypes").PreflightPlan> {
    return request(`/api/v1/preflight`, {
      method: "POST",
      body: JSON.stringify({
        account_id: args.accountId,
        template_id: args.templateId,
        version_id: args.versionId,
        intent: args.intent,
        inputs: args.inputs,
        connector_states: [],
        available_secrets: [],
      }),
    });
  },

  getFeedback(accountId: string, runId: string): Promise<import("./runTypes").RunFeedback | null> {
    return request(`/api/v1/runs/${runId}/feedback?account_id=${accountId}`);
  },

  setFeedback(
    accountId: string,
    runId: string,
    feedback: string,
  ): Promise<import("./runTypes").RunFeedback> {
    return request(`/api/v1/runs/${runId}/feedback`, {
      method: "POST",
      body: JSON.stringify({ account_id: accountId, feedback }),
    });
  },

  artifacts(accountId: string, runId: string): Promise<ArtifactRecord[]> {
    return request(`/api/v1/runs/${runId}/artifacts?account_id=${accountId}`);
  },

  resolveApproval(
    accountId: string,
    runId: string,
    approvalId: string,
    decision: "approved" | "denied" | "guided",
    reason?: string,
    guidance?: string,
  ): Promise<{ resolved: boolean }> {
    return request(`/api/v1/runs/${runId}/approvals/${approvalId}`, {
      method: "POST",
      body: JSON.stringify({ account_id: accountId, decision, reason, guidance }),
    });
  },

  proposals(accountId: string, status?: string): Promise<PostRunProposal[]> {
    const params = new URLSearchParams({ account_id: accountId });
    if (status) params.set("status", status);
    return request(`/api/v1/proposals?${params.toString()}`);
  },

  applyProposal(accountId: string, proposalId: string): Promise<{ applied_version_id?: string }> {
    return request(`/api/v1/proposals/${proposalId}/apply`, {
      method: "POST",
      body: JSON.stringify({ account_id: accountId }),
    });
  },

  rejectProposal(accountId: string, proposalId: string, comment: string): Promise<PostRunProposal> {
    return request(`/api/v1/proposals/${proposalId}/reject`, {
      method: "POST",
      body: JSON.stringify({ account_id: accountId, comment }),
    });
  },

  artifactUrl(accountId: string, artifactId: string): string {
    return `${apiBaseUrl()}/api/v1/artifacts/${artifactId}?account_id=${accountId}`;
  },
};

/** Downloads through the authenticated route; never exposes a public URL. */
export async function downloadArtifact(accountId: string, artifactId: string, filename: string) {
  const response = await fetch(runApi.artifactUrl(accountId, artifactId), {
    headers: { authorization: `Bearer ${authToken()}` },
  });
  if (!response.ok) throw new Error(`artifact download failed: ${response.status}`);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/** SSE replay + live tail with an explicit cursor (fetch cannot set Last-Event-ID). */
export async function streamRunEvents(args: {
  accountId: string;
  runId: string;
  cursor: number;
  signal: AbortSignal;
  onEvent: (event: RunEventRecord) => void;
  onRetry: (cursor: number) => void;
}): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/runs/${args.runId}/events?account_id=${args.accountId}&cursor=${args.cursor}`,
    {
      headers: { authorization: `Bearer ${authToken()}` },
      signal: args.signal,
    },
  );
  if (!response.ok || !response.body) {
    throw new Error(`event stream failed: ${response.status}`);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let boundary = buffer.indexOf("\n\n");
    while (boundary >= 0) {
      const frame = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      let kind = "message";
      const data: string[] = [];
      for (const line of frame.split("\n")) {
        if (line.startsWith("event:")) kind = line.slice(6).trim();
        else if (line.startsWith("data:")) data.push(line.slice(5).trim());
      }
      if (!data.length) continue;
      if (kind === "retry") {
        try {
          const parsed = JSON.parse(data.join("\n")) as { cursor?: number };
          args.onRetry(parsed.cursor ?? args.cursor);
        } catch {
          args.onRetry(args.cursor);
        }
        continue;
      }
      try {
        args.onEvent(JSON.parse(data.join("\n")) as RunEventRecord);
      } catch {
        // Ignore a malformed frame; the cursor replay will recover it.
      }
      boundary = buffer.indexOf("\n\n");
    }
  }
}
